import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { useCopyToWorkspace } from '~/components/CopyToWorkspace/hooks/useCopyToWorkspace';
import { ExportImagesModal } from '~/components/ExportImagesModal/ExportImagesModal';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';
import {
  centralizedClipAssetIdSelector,
  centralizedClipAssetsSelector,
  centralizedClipDimensionsSelector,
  centralizedClipExtSelector,
  centralizedClipIdSelector,
  centralizedDisplayTitleSelector,
} from '~/store/centralizedClip/selectors';
import { extCanBeExported, getBestImgixExportSource } from '~/utils/Imgix';
import { canDownloadVersion } from '~/utils/permissions/assetPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export const usePublicDownloadOptions = () => {
  const store = useAirStore();
  const clipId = useSelector(centralizedClipIdSelector);
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const ext = useSelector(centralizedClipExtSelector);
  const [showExportImagesModal] = useAirModal(ExportImagesModal);
  const { copyToAir } = useCopyToWorkspace();
  const { permissions } = usePublicPermissions();
  const assets = useSelector(centralizedClipAssetsSelector);
  const exportSource = getBestImgixExportSource(ext, assets);

  const { canViewAllVersions: canViewAssetVersions } = useCanViewAllVersionsOnPublicShareLink();
  const canDownload = canDownloadVersion(permissions);

  const onSelectExportModal = useMemo(
    () =>
      extCanBeExported(ext) && exportSource && canDownload
        ? () => {
            const fileName = centralizedDisplayTitleSelector(store.getState());
            const { height, width } = centralizedClipDimensionsSelector(store.getState());

            return showExportImagesModal({
              fileName,
              height,
              imageSrc: exportSource,
              width,
            });
          }
        : undefined,
    [canDownload, ext, exportSource, showExportImagesModal, store],
  );

  const onSelectCopy = useMemo(
    () => () => {
      /**
       * We only allow the entire asset to be copied if canViewAssetVersions
       * is true. Else, we just let them copy that single clip (asset version)
       */
      return canViewAssetVersions && assetId
        ? copyToAir({
            assetId,
            trackLocation: 'public-asset-page',
          })
        : copyToAir({
            clipId,
            trackLocation: 'public-asset-page',
          });
    },
    [assetId, canViewAssetVersions, clipId, copyToAir],
  );

  return useMemo(() => {
    const options: DropdownMenuOption[] = [];

    options.push({
      id: 'copy',
      label: 'Copy to Air workspace',
      onSelect: onSelectCopy,
      type: 'item',
    });

    if (onSelectExportModal) {
      options.push({
        id: 'export',
        label: 'Export as',
        onSelect: onSelectExportModal,
        type: 'item',
      });
    }

    return options;
  }, [onSelectCopy, onSelectExportModal]);
};
